import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, getDoc, orderBy, limit, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Trophy, Medal, Award, TrendingUp, Star, Zap, Crown } from 'lucide-react';

const BettingLeaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // day, week, month, all
  const [metric, setMetric] = useState('quickPicks'); // bets, winnings, quickPicks

  useEffect(() => {
    // TODO: Will be replaced with Cloud Function call
    // loadLeaderboard();
    setLoading(false); // For now, just show "Coming Soon"
  }, [timeRange, metric]);

  const loadLeaderboard = async () => {
    // ============================================
    // TODO: FUTURE IMPLEMENTATION
    // ============================================
    // This will be replaced with a Cloud Function call to avoid permission issues
    // The Cloud Function will:
    // 1. Aggregate predictionUsage data server-side
    // 2. Count quickPicks from predictionUsage/{userId}/events
    // 3. Count bets and winnings from bettingPools
    // 4. Return top 10 users with displayNames
    //
    // Example implementation:
    // const getLeaderboard = httpsCallable(functions, 'getLeaderboard');
    // const result = await getLeaderboard({ metric, timeRange });
    // setLeaderboardData(result.data);
    //
    // For now, commenting out direct Firestore queries due to permission restrictions
    // ============================================

    /* COMMENTED OUT - Will be moved to Cloud Function
    setLoading(true);
    try {
      const userBetCounts = {};
      const userWinnings = {};
      const userQuickPicks = {};
      const userNames = {};

      // Calculate time cutoff
      const now = new Date();
      let cutoffTime = 0;
      if (timeRange === 'day') {
        cutoffTime = now.getTime() - (24 * 60 * 60 * 1000);
      } else if (timeRange === 'week') {
        cutoffTime = now.getTime() - (7 * 24 * 60 * 60 * 1000);
      } else if (timeRange === 'month') {
        cutoffTime = now.getTime() - (30 * 24 * 60 * 60 * 1000);
      }

      // Get quick picks from predictionUsage collection
      if (metric === 'quickPicks') {
        // Get all users who have made predictions
        const usersRef = collection(db, 'predictionUsage');
        const usersSnapshot = await getDocs(usersRef);

        for (const userDoc of usersSnapshot.docs) {
          const userId = userDoc.id;

          // Query events for this user within time range
          const eventsRef = collection(db, `predictionUsage/${userId}/events`);
          let eventsQuery = query(eventsRef, where('eventType', '==', 'prediction_made'));

          if (timeRange !== 'all') {
            const cutoffTimestamp = Timestamp.fromMillis(cutoffTime);
            eventsQuery = query(
              eventsRef,
              where('eventType', '==', 'prediction_made'),
              where('timestamp', '>=', cutoffTimestamp)
            );
          }

          const eventsSnapshot = await getDocs(eventsQuery);
          let quickPickCount = 0;

          eventsSnapshot.forEach(doc => {
            const event = doc.data();
            // Count quick predictions specifically
            if (event.feature === 'quick_prediction') {
              quickPickCount += (event.gamesCount || 0);
            }
          });

          if (quickPickCount > 0) {
            userQuickPicks[userId] = quickPickCount;
          }
        }
      } else {
        // Get betting data for bets and winnings metrics
        const poolsRef = collection(db, 'bettingPools');
        const poolsSnapshot = await getDocs(poolsRef);

        for (const poolDoc of poolsSnapshot.docs) {
          const poolData = poolDoc.data();

          // Get all bets in this pool
          const betsRef = collection(db, `bettingPools/${poolDoc.id}/bets`);
          const betsSnapshot = await getDocs(betsRef);

          for (const betDoc of betsSnapshot.docs) {
            const betData = betDoc.data();
            const userId = betDoc.id;

            // Check if bet is within time range
            if (betData.timestamp && betData.timestamp.toMillis() >= cutoffTime) {
              // Count bets
              userBetCounts[userId] = (userBetCounts[userId] || 0) + 1;

              // Track winnings if pool is settled
              if (poolData.status === 'settled' && betData.payout > 0) {
                userWinnings[userId] = (userWinnings[userId] || 0) + betData.payout;
              }
            }
          }
        }
      }

      // Determine which users to fetch based on metric
      let userIds = [];
      if (metric === 'quickPicks') {
        userIds = Object.keys(userQuickPicks);
      } else {
        userIds = Object.keys(userBetCounts);
      }

      // Get user display names
      for (const userId of userIds) {
        try {
          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Use displayName if available, otherwise use anonymous handle
            userNames[userId] = userData.displayName || `Player_${userId.slice(0, 6)}`;
          } else {
            userNames[userId] = `Player_${userId.slice(0, 6)}`;
          }
        } catch (error) {
          console.warn(`Failed to fetch user ${userId}:`, error);
          userNames[userId] = `Player_${userId.slice(0, 6)}`;
        }
      }

      // Build leaderboard data
      const leaderboardEntries = userIds.map(userId => ({
        userId,
        displayName: userNames[userId],
        betCount: userBetCounts[userId] || 0,
        winnings: userWinnings[userId] || 0,
        quickPicks: userQuickPicks[userId] || 0
      }));

      // Sort by selected metric
      leaderboardEntries.sort((a, b) => {
        if (metric === 'bets') return b.betCount - a.betCount;
        if (metric === 'winnings') return b.winnings - a.winnings;
        if (metric === 'quickPicks') return b.quickPicks - a.quickPicks;
        return 0;
      });

      // Take top 10
      setLeaderboardData(leaderboardEntries.slice(0, 10));
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
    */
  };

  const getRankIcon = (rank) => {
    switch(rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-300" />;
      case 3:
        return <Award className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="text-white/60 font-bold">#{rank}</span>;
    }
  };

  const getMetricLabel = () => {
    switch(metric) {
      case 'bets':
        return 'Bets Placed';
      case 'winnings':
        return 'Total Winnings';
      case 'quickPicks':
        return 'Quick Picks';
      default:
        return 'Score';
    }
  };

  const getMetricValue = (entry) => {
    switch(metric) {
      case 'bets':
        return `${entry.betCount} bets`;
      case 'winnings':
        return `${entry.winnings} ⭐`;
      case 'quickPicks':
        return `${entry.quickPicks} picks`;
      default:
        return '0';
    }
  };

  return (
    <div style={{
      background: '#0A0A0A',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '0'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{
            fontSize: '10px',
            fontWeight: '800',
            color: 'rgba(255, 255, 255, 0.5)',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            marginBottom: '8px'
          }}>
            LEADERBOARD
          </h2>
          <p style={{
            fontSize: '8px',
            color: 'rgba(255, 255, 255, 0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            TOP PERFORMERS • {timeRange === 'all' ? 'ALL TIME' : timeRange.toUpperCase()}
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {/* Time Range */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {['DAY', 'WEEK', 'MONTH', 'ALL'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range.toLowerCase())}
                style={{
                  padding: '8px 16px',
                  background: timeRange === range.toLowerCase() || (timeRange === 'all' && range === 'ALL') ? '#8B5CF6' : 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: timeRange === range.toLowerCase() || (timeRange === 'all' && range === 'ALL') ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
                  fontSize: '8px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {range}
              </button>
            ))}
          </div>

          {/* Metric */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { id: 'quickPicks', label: 'QUICK PICKS' },
              { id: 'bets', label: 'MOST BETS' },
              { id: 'winnings', label: 'TOP WINNINGS' }
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMetric(m.id)}
                style={{
                  padding: '8px 16px',
                  background: metric === m.id ? '#8B5CF6' : 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: metric === m.id ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
                  fontSize: '8px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Leaderboard List */}
      <div style={{ padding: '24px' }}>
        {/* Coming Soon Message */}
        <div style={{
          textAlign: 'center',
          padding: '48px 0'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px'
          }}>
            <Trophy style={{
              width: '48px',
              height: '48px',
              color: 'rgba(255, 255, 255, 0.1)'
            }} />
            <div>
              <h3 style={{
                fontSize: '10px',
                fontWeight: '900',
                color: 'rgba(255, 255, 255, 0.5)',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                marginBottom: '16px'
              }}>
                COMING SOON
              </h3>
              <p style={{
                fontSize: '8px',
                color: 'rgba(255, 255, 255, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                maxWidth: '400px',
                lineHeight: '1.6'
              }}>
                TRACK YOUR RANKING AGAINST OTHER PLAYERS • LEADERBOARDS FOR QUICK PICKS, MOST BETS, AND TOP WINNINGS WILL BE AVAILABLE SHORTLY
              </p>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '16px'
            }}>
              <div style={{
                width: '4px',
                height: '4px',
                background: 'rgba(139, 92, 246, 0.3)',
                animation: 'pulse 2s infinite'
              }}></div>
              <div style={{
                width: '4px',
                height: '4px',
                background: 'rgba(139, 92, 246, 0.3)',
                animation: 'pulse 2s infinite',
                animationDelay: '0.5s'
              }}></div>
              <div style={{
                width: '4px',
                height: '4px',
                background: 'rgba(139, 92, 246, 0.3)',
                animation: 'pulse 2s infinite',
                animationDelay: '1s'
              }}></div>
            </div>
          </div>
        </div>

        {/* Commented out original leaderboard display - will be re-enabled when Cloud Function is ready */}

      </div>
    </div>
  );
};

export default BettingLeaderboard;